<script setup>
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import Modal from "@/Components/Modal.vue";
import Button from "@/Components/Button.vue";
import Input from "@/Components/Input.vue";

const open = ref(false);
const submitted = ref(false);
const processing = ref(false);

const email = ref("");

function submit() {
    processing.value = true;

    router.post(
        route("auth.email"),
        {
            email: email.value,
        },
        {
            onSuccess: () => {
                submitted.value = true;
            },
            onFinish: () => {
                processing.value = false;
            },
        }
    );
}

function reset() {
    submitted.value = false;
    email.value = null;
}
</script>

<template>
    <div>
        <div class="items-center sm:flex">
            <button
                class="my-2 mr-2.5 text-sm font-semibold text-white underline"
                @click="open = true"
            >
                Sign in with Magic Link &rarr;
            </button>
        </div>

        <Modal :open="open" mini @close="open = false">
            <h4
                class="mb-2.5 font-display text-xl font-bold tracking-tight text-brand-900"
            >
                Sign in with Magic Link
            </h4>

            <section
                v-if="submitted"
                class="text-sm leading-relaxed text-gray-500"
            >
                <p class="mb-2.5 mt-5 text-gray-600">
                    We've sent a magic sign-in link to
                    <strong class="font-semibold text-brand-900">{{
                        email
                    }}</strong
                    >.
                </p>

                <p class="mb-2.5 mt-5 text-gray-600">
                    Click the link in the email we sent you to sign in to your
                    account.
                </p>

                <p class="mt-5 pb-2.5 text-gray-600">
                    If you don't receive an email, please check your spam
                    folder. If you still don't see it, there may not be an
                    account associated with your email address.
                </p>

                <div class="mt-5 flex items-center space-x-2">
                    <Button variant="light" @click="open = close">Close</Button>
                    <Button @click="reset"> Send another link </Button>
                </div>
            </section>

            <form v-else @submit.prevent="submit">
                <section class="mb-5 text-sm leading-relaxed text-gray-500">
                    <p class="mb-2.5 mt-5 text-gray-600">
                        We'll email you a magic link to sign in to your account
                        instead.
                    </p>

                    <p class="mb-2.5 mt-5 text-gray-600">
                        Magic sign-in links are only available for existing
                        accounts that already have an email address associated
                        with them.
                    </p>

                    <Input
                        v-model="email"
                        class="mt-5"
                        placeholder="Enter your email address"
                        autofocus
                        type="email"
                        required
                    />
                </section>

                <div class="flex items-center space-x-2">
                    <Button variant="light" @click="open = close"
                        >Cancel</Button
                    >
                    <Button type="submit" :disabled="processing">
                        <font-awesome-icon
                            v-if="processing"
                            :icon="['fas', 'circle-notch']"
                            class="mr-2.5 h-4 w-4 animate-spin text-gray-300"
                            aria-hidden="true"
                        />
                        {{ processing ? "Sending..." : "Send me a magic link" }}
                    </Button>
                </div>
            </form>
        </Modal>
    </div>
</template>
